/**
 * CSV export helpers (#18). Every cell is quoted with inner quotes doubled, formula-leading
 * text is neutralized, rows end in CRLF, and the file starts with a BOM so Excel reads it as UTF-8.
 */

export type CsvCell = string | number | boolean | null | undefined;

/**
 * Text that a spreadsheet would read as a formula (=, +, -, @, or a tab or carriage return,
 * also after leading spaces) gets an apostrophe in front, so a contract title like `=1+1`
 * is shown, not evaluated, when another user opens the export. Numbers are left alone.
 */
const FORMULA_START = /^[\s]*[=+\-@]|^[\t\r]/;

function neutralize(cell: string): string {
  return FORMULA_START.test(cell) ? `'${cell}` : cell;
}

function quote(cell: CsvCell): string {
  if (cell === null || cell === undefined) return '""';
  const text = typeof cell === 'string' ? neutralize(cell) : String(cell);
  return `"${text.replace(/"/g, '""')}"`;
}

/** Builds the CSV text for a header row and data rows. */
export function toCsv(headers: string[], rows: CsvCell[][]): string {
  return [headers, ...rows].map((row) => row.map(quote).join(',')).join('\r\n');
}

/** Builds the CSV and triggers a browser download of it. */
export function downloadCsv(filename: string, headers: string[], rows: CsvCell[][]): void {
  const blob = new Blob(['\uFEFF' + toCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
